import posthog from 'posthog-js';
import memoize from 'lodash/memoize';
import Cookies from 'universal-cookie';
import { UserProfileResource } from 'util/resources/UserProfile';
import { GeographyResource } from 'util/resources/Geography';
import { EducationHistoryResource } from 'util/resources/EducationHistory';
import { WorkHistoryResource } from 'util/resources/WorkHistory';
import { CertificationResource } from 'util/resources/Certification';
import { EmployerResource } from 'util/resources/Employer';
import { SubscriptionResource } from 'util/resources/Subscription';
import { EmployerProfileResource } from 'util/resources/EmployerProfile';
import getConfig from 'next/config';
import { debounce } from 'lodash';

const publicRuntimeConfig = getConfig?.()?.publicRuntimeConfig;
const posthogKey = publicRuntimeConfig?.posthogKey;

const postHogIdentify: typeof posthog.identify = memoize(
  (...args: Parameters<typeof posthog.identify>) => posthog.identify(...args),
  (...args) => {
    const arrOfString = args.map((arg) => {
      if (typeof arg !== 'string') {
        return JSON.stringify(arg);
      }
      return arg;
    });

    return arrOfString.join('-');
  },
).bind(posthog) as typeof posthog.identify;

const getDistinctId = (): string => {
  const cookies = new Cookies();
  const allCookies = cookies.getAll() as Record<string, string>;
  const envVar = posthogKey;
  const key = Object.keys(allCookies).find((key) => key.includes(envVar));
  const jsonData = cookies.get(key) as Record<string, string>;

  return jsonData?.distinct_id;
};

export const postHogReset: typeof posthog.reset = (resetDeviceId) => posthog.reset(resetDeviceId);

export const postHogIdentifyUser = (
  user: UserProfileResource,
  geography: GeographyResource,
  educations: EducationHistoryResource[],
  workHistories: WorkHistoryResource[],
  certifications: CertificationResource[],
) => {
  try {
    if (user?.id) {
      const distinctId = getDistinctId();

      postHogIdentify(user.id, {
        email: user.email,
        'First Name': user.firstName,
        'Last Name': user.lastName,
        'Created At': user.createdAt,
        'Approval State': user.approvalState,
        Geography: geography?.longName,
        'Active State': user.activeState,
        'Indeed Resume': user.indeedResume,
        'Missing Required Fields': user.missingRequiredFields,
        'Visibility State': user.visibilityState,
        'Work History Count': workHistories?.length || 0,
        'Education Count': educations?.length || 0,
        'Certifications Count': certifications?.length || 0,
      });

      if (Boolean(distinctId) && user.id !== distinctId) {
        posthog.alias(user.id, distinctId);
      }
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('POSTHOG ERROR', e);
  }
};

export const postHogIdentifyEmployer = (
  employer: EmployerResource,
  subscription: SubscriptionResource,
  geography: GeographyResource,
  currentEmployerProfile: EmployerProfileResource,
) => {
  try {
    if (employer?.id) {
      const distinctId = getDistinctId();
      const planName = subscription?.plan()?.name;
      const status = subscription?.attributes()?.status;

      postHogIdentify(employer.id, {
        email: employer.email,
        'Employer Name': employer.name,
        'Created At': employer.createdAt,
        Geography: geography?.longName,
        'First Name': currentEmployerProfile?.firstName,
        'Last Name': currentEmployerProfile?.lastName,
        'Plan Name': planName,
        'Subscription Status': status,
      });

      if (Boolean(distinctId) && employer.id !== distinctId) {
        posthog.alias(employer.id, distinctId);
      }
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('POSTHOG ERROR', e);
  }
};

export const posthogCapture = (debounceInMs = 0): typeof posthog.capture =>
  debounceInMs
    ? debounce<typeof posthog.capture>((...args) => posthog.capture(...args), debounceInMs)
    : (...args) => posthog.capture(...args);
